/**
 * Vertical scrolling turn list. Flat layout — no dividers, no chat
 * bubbles. User turns render as inset grey cards (see
 * :class:`UserPrompt`); assistant turns flow flush below. Subagent
 * progress is surfaced inline on the spawning ``Task`` tool-use card,
 * so there is no separate bottom rail.
 *
 * Scroll model (rev 2026-05-28):
 *
 *   - Auto-follow ("keep snapping to the floor as content arrives")
 *     is owned by ``handleScroll`` reading scroll *direction*
 *     (:func:`decideScrollFollow`). A reader moving up off the floor
 *     releases it; settling back at the floor re-engages it. This is
 *     the only signal that survives rapid streaming, where the floor
 *     moves between a programmatic snap and the scroll event firing --
 *     an absolute distance-from-floor check leaked there and falsely
 *     released. Our own programmatic writes are tagged
 *     (``programmaticScrollRef``) so a snap echo or a content-shrink
 *     clamp never reads as a wheel-up.
 *   - On any layout shift (``ResizeObserver``) or live content commit,
 *     the rule is binary: following → snap to the floor; released →
 *     preserve a visible turn anchor. Covers streaming tokens,
 *     expanding tool blocks, thinking-block growth, image measure-in,
 *     code-block reflow, composer textarea growth, and window resize.
 *     Both are held off while the reader is actively dragging
 *     (``userScrollingRef``) so they never grab the viewport mid-scroll.
 *   - The Latest pill is a separate, geometry-driven question answered
 *     by an :js:class:`IntersectionObserver` plus ``handleScroll``,
 *     both routed through a Schmitt trigger (:func:`decideChipVisible`)
 *     so the show/hide boundary can't flicker.
 *   - Deliberate jumps (user sent a message, Latest pill click) use a
 *     short eased glide that follows the moving bottom every frame.
 *     While the glide runs, ``pinningRef`` gates the observers off so
 *     they don't fight the tween.
 */

import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import type { AgentPanelState } from '@/lib/agent/reducer';
import {
  getActiveTaskByToolUse,
  getSubagentByToolUse,
  getWorkingIndicatorModel,
  isCompactPending,
  isToolUsePending,
  shouldShowNoResponseHint,
} from '@/lib/agent/reducer';
import type { AgentStarterPrompt } from '@/lib/agent/starterPrompts';
import { cn } from '@/lib/utils/cn';
import { AgentChatEmptyState } from './AgentChatEmptyState';
import { AssistantStream } from './AssistantStream';
import { AutoExpandImagesProvider, collectRecentImageBlocks } from './blocks/ImageBlockView';
import { InteractionPanels } from './InteractionPanels';
import { SystemNoticeLine } from './SystemNoticeLine';
import { UserPrompt } from './UserPrompt';
import { WorkingIndicator } from './WorkingIndicator';

/**
 * Duration of the deliberate "user just sent" / "Latest" glide.
 *
 * Tuned short and paired with ``easeOutCubic`` so the chat catches up
 * to the bottom quickly even when the agent starts streaming inside
 * the glide window. Streaming growth during the glide is followed by
 * the per-frame ``getTarget()`` recompute in :func:`animateScrollTo`,
 * so the tween's endpoint is always the *current* bottom rather than
 * the bottom at glide-start.
 */
const SMOOTH_PIN_DURATION_MS = 260;
const USER_SUBMIT_PIN_DURATION_MS = 360;
/**
 * Entry-animation duration for the just-submitted user prompt. Sits
 * a hair longer than the matching CSS animation in the render so the
 * wrapper class can survive the full animation even if React commits
 * a few extra renders during the glide.
 */
const USER_SUBMIT_ENTER_HOLD_MS = 420;
const SCROLL_ANCHOR_SELECTOR = '[data-turn-stream-anchor]';
const SCROLL_ANCHOR_TOP_INSET_PX = 24;
/**
 * Bottom-edge slack (in pixels) applied to the bottom sentinel's
 * :js:class:`IntersectionObserver`. The sentinel is a 1px element at
 * the very bottom of the inner column; with
 * ``rootMargin = '0px 0px ${AT_BOTTOM_SLOP_PX}px 0px'`` the browser
 * considers it "intersecting" whenever it sits within
 * ``AT_BOTTOM_SLOP_PX`` pixels of the viewport's bottom edge.
 *
 * This is the SHOW threshold of the Latest pill -- "atBottom" is a
 * visual question (should the chip be visible?) that wants a
 * generous band so trackpad wobble and sub-pixel jitter don't
 * flicker the chip. 240px = roughly one paragraph of body text
 * (~10 lines at 24px line-height, or 3-5 tool/text blocks), which
 * absorbs that noise without hiding the chip while the reader is
 * meaningfully detached. The chip only HIDES again once the reader
 * is back within the tighter ``CHIP_HIDE_BAND_PX`` -- the gap
 * between the two is the Schmitt trigger that kills the boundary
 * flicker (see :func:`decideChipVisible`).
 *
 * Auto-follow is a separate concern owned entirely by scroll
 * direction in ``handleScroll`` (see :func:`decideScrollFollow`),
 * not this band -- so a small wheel-up within the slop releases
 * follow instead of letting the next streamed chunk yank the
 * reader back to the floor (the old "spazz").
 */
export const AT_BOTTOM_SLOP_PX = 240;

/**
 * Lower threshold of the Latest-chip hysteresis band, in pixels.
 *
 * The chip SHOWS once the reader is more than ``AT_BOTTOM_SLOP_PX``
 * from the floor and only HIDES again once they are back within
 * ``CHIP_HIDE_BAND_PX``. The gap between the two thresholds is a
 * Schmitt trigger: sub-pixel trackpad wobble sitting on a single
 * boundary can no longer toggle the chip on and off every other
 * frame (the boundary flicker). See :func:`decideChipVisible`.
 */
export const CHIP_HIDE_BAND_PX = 150;

/**
 * Minimum downward-relative ``scrollTop`` decrease (in pixels) that
 * counts as a deliberate move off the floor. Smaller deltas are
 * sub-pixel rounding or a content-shrink clamp, not a wheel-up.
 */
export const SCROLL_UP_RELEASE_PX = 2;

/**
 * How close to the floor (in pixels) the reader must return before
 * auto-follow re-engages. Intentionally tight: a reader who scrolled
 * up and parked stays released until they come essentially all the
 * way back to the bottom, so a stray scroll event near their parked
 * position can never re-stick them mid-read.
 */
export const FOLLOW_REENGAGE_BAND_PX = 8;

/**
 * Quiet period (ms) after the last scroll event before the reader is
 * considered idle again. While "actively scrolling" the resize /
 * content observers stay out of the way so they never grab the
 * viewport out from under a drag; a final reconcile runs once the
 * reader settles.
 */
export const USER_SCROLL_IDLE_MS = 140;

/** Stable no-op for the interaction-respond prop so an absent handler
 * doesn't hand a fresh function identity to the panel every render. */
const NOOP_RESPOND_TO_INTERACTION = () => undefined;

export type FollowDecision = 'release' | 'reengage' | 'keep';

/**
 * Decide how a single scroll event should affect auto-follow.
 *
 * The only signal that reliably separates a deliberate reader move
 * from streaming growth is the *direction* of ``scrollTop``: a reader
 * moving up always decreases it, while content growth never touches
 * ``scrollTop`` (only ``scrollHeight`` grows) and so fires no scroll
 * event at all. That is why the old absolute-distance check leaked --
 * during fast streaming the floor moved between a programmatic snap
 * and the scroll event firing, so ``fromFloor`` read non-zero and
 * follow released even though the reader never touched the wheel.
 *
 * A downward ``scrollTop`` change that leaves the reader still glued
 * to the floor is a content-shrink clamp (a thinking block contracts,
 * the composer collapses, the window grows): the browser pulls
 * ``scrollTop`` down to the new, shorter floor without the reader
 * moving. That must NOT release, which is why release additionally
 * requires the reader to now sit off the floor.
 *
 * :param prevTop: ``scrollTop`` at the previous scroll event.
 * :param nextTop: ``scrollTop`` now.
 * :param fromFloor: ``bottomScrollTop(el) - nextTop`` now.
 * :param reengageBand: re-engage when at or under this distance.
 * :return: ``release`` to stop following, ``reengage`` to resume, or
 *     ``keep`` to leave the current follow state untouched.
 */
export function decideScrollFollow(
  prevTop: number,
  nextTop: number,
  fromFloor: number,
  reengageBand: number
): FollowDecision {
  const movedUp = prevTop - nextTop > SCROLL_UP_RELEASE_PX;
  if (movedUp && fromFloor > SCROLL_UP_RELEASE_PX) return 'release';
  if (!movedUp && fromFloor <= reengageBand) return 'reengage';
  return 'keep';
}

/**
 * Schmitt-trigger visibility for the Latest chip.
 *
 * :param prevVisible: whether the chip is currently shown.
 * :param fromFloor: distance from the bottom, in pixels.
 * :param showAt: show once farther than this from the floor.
 * :param hideAt: hide once back within this of the floor.
 * :return: whether the chip should be visible.
 */
export function decideChipVisible(
  prevVisible: boolean,
  fromFloor: number,
  showAt: number = AT_BOTTOM_SLOP_PX,
  hideAt: number = CHIP_HIDE_BAND_PX
): boolean {
  if (prevVisible) return fromFloor > hideAt;
  return fromFloor > showAt;
}

interface RememberedScrollPosition {
  scrollTop: number;
  atBottom: boolean;
  revision: number;
}

interface ScrollAnchor {
  key: string;
  top: number;
}

const REMEMBERED_SCROLL_POSITIONS = new Map<string, RememberedScrollPosition>();

/**
 * Cubic ease-out. At t=0.5 the eased value is 0.875, so the tween is
 * mostly at the target by the midpoint — the agent often starts
 * streaming inside the glide window and we don't want scrollTop
 * trailing the actual bottom by half the gap.
 */
function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function bottomScrollTop(el: HTMLElement): number {
  return Math.max(0, el.scrollHeight - el.clientHeight);
}

function captureScrollAnchor(root: HTMLElement): ScrollAnchor | null {
  const rootRect = root.getBoundingClientRect();
  const anchors = root.querySelectorAll<HTMLElement>(SCROLL_ANCHOR_SELECTOR);
  let fallback: ScrollAnchor | null = null;
  for (const anchor of anchors) {
    const key = anchor.dataset.turnStreamAnchor;
    if (!key) continue;
    const rect = anchor.getBoundingClientRect();
    if (rect.height <= 0 || rect.bottom < rootRect.top + 1) continue;
    if (rect.top > rootRect.bottom) continue;
    const candidate = { key, top: rect.top - rootRect.top };
    fallback ??= candidate;
    if (rect.top >= rootRect.top + SCROLL_ANCHOR_TOP_INSET_PX) return candidate;
  }
  return fallback;
}

function restoreScrollAnchor(root: HTMLElement, anchor: ScrollAnchor | null): boolean {
  if (!anchor) return false;
  const anchors = root.querySelectorAll<HTMLElement>(SCROLL_ANCHOR_SELECTOR);
  const target = Array.from(anchors).find((candidate) => candidate.dataset.turnStreamAnchor === anchor.key);
  if (!target) return false;
  const rootRect = root.getBoundingClientRect();
  const nextTop = target.getBoundingClientRect().top - rootRect.top;
  const delta = nextTop - anchor.top;
  if (Math.abs(delta) > 0.5) {
    root.scrollTop += delta;
  }
  return true;
}

/**
 * rAF-based scrollTop tween. ``getTarget`` is invoked every frame so
 * the animation tracks a moving destination (the bottom of a
 * streaming chat keeps moving as tokens arrive — the tween must
 * follow). Returns a cancel function the caller can invoke to
 * abandon the animation mid-flight.
 */
function animateScrollTo(
  el: HTMLElement,
  getTarget: () => number,
  durationMs: number,
  onComplete: () => void
): () => void {
  if (durationMs <= 0) {
    el.scrollTop = getTarget();
    onComplete();
    return () => undefined;
  }
  const start = el.scrollTop;
  const startTime = performance.now();
  let cancelled = false;
  let rafId = 0;
  function step(now: number) {
    if (cancelled) return;
    const t = Math.min(1, (now - startTime) / durationMs);
    const eased = easeOutCubic(t);
    const target = getTarget();
    el.scrollTop = start + (target - start) * eased;
    if (t < 1) {
      rafId = requestAnimationFrame(step);
    } else {
      el.scrollTop = getTarget();
      onComplete();
    }
  }
  rafId = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
  };
}

/**
 * Repeated "snap to bottom" pins across the first ~700ms of mount.
 * Covers late-loading content: images measuring in, code blocks
 * reflowing, web fonts swapping the layout out from under the
 * initial pin. The ResizeObserver below would catch most of these
 * once it's running, but this schedule guarantees the first paint
 * lands at the bottom even if RO hasn't fired its first callback
 * yet.
 */
function scheduleSettlingBottomPin(pin: () => void): () => void {
  let cancelled = false;
  const rafIds: number[] = [];
  const timers: ReturnType<typeof setTimeout>[] = [];

  const doPin = () => {
    if (cancelled) return;
    pin();
  };

  doPin();
  rafIds.push(
    requestAnimationFrame(() => {
      doPin();
      rafIds.push(requestAnimationFrame(doPin));
    })
  );
  for (const delay of [50, 150, 350, 700]) {
    timers.push(setTimeout(doPin, delay));
  }

  return () => {
    cancelled = true;
    for (const rafId of rafIds) cancelAnimationFrame(rafId);
    for (const timer of timers) clearTimeout(timer);
  };
}

function latestAssistantTurnId(turns: AgentPanelState['turns']): string | null {
  for (let index = turns.length - 1; index >= 0; index--) {
    if (turns[index]?.role === 'assistant') return turns[index].id;
  }
  return null;
}

type TimelineEntry =
  | {
      kind: 'turn';
      turn: AgentPanelState['turns'][number];
      previousTurnRole: AgentPanelState['turns'][number]['role'] | null;
    }
  | { kind: 'notice'; notice: AgentPanelState['notices'][number] };

/**
 * Build the render timeline without ever reordering ``state.turns``.
 *
 * ``state.turns`` is already the canonical transcript order after
 * ``prependHistory`` merges older REST pages with the live tail.
 * Sorting turns by ``startedAt`` is incorrect: history-page turns are
 * reconstructed when the page loads, so their timestamps can be newer
 * than the live tail and they would get appended to the bottom of the
 * chat. Notices instead carry ``anchorTurnId`` from the reducer: render
 * each notice immediately after the turn that was at the tail when the
 * notice arrived, and put orphan/unanchored notices at the end so they
 * remain visible without disturbing the transcript order.
 */
export function buildTimeline(turns: AgentPanelState['turns'], notices: AgentPanelState['notices']): TimelineEntry[] {
  const renderableNotices = notices.filter((notice) => notice.type !== 'init');
  if (renderableNotices.length === 0) {
    let previousTurnRole: AgentPanelState['turns'][number]['role'] | null = null;
    return turns.map((turn) => {
      const entry: TimelineEntry = { kind: 'turn', turn, previousTurnRole };
      previousTurnRole = turn.role;
      return entry;
    });
  }

  const turnIds = new Set(turns.map((turn) => turn.id));
  const noticesByAnchor = new Map<string, AgentPanelState['notices']>();
  const orphanNotices: AgentPanelState['notices'] = [];

  for (const notice of renderableNotices) {
    const anchorTurnId = notice.anchorTurnId ?? null;
    if (anchorTurnId && turnIds.has(anchorTurnId)) {
      const anchored = noticesByAnchor.get(anchorTurnId) ?? [];
      anchored.push(notice);
      noticesByAnchor.set(anchorTurnId, anchored);
    } else {
      orphanNotices.push(notice);
    }
  }

  const entries: TimelineEntry[] = [];
  let previousTurnRole: AgentPanelState['turns'][number]['role'] | null = null;
  for (const turn of turns) {
    entries.push({ kind: 'turn', turn, previousTurnRole });
    const anchoredNotices = noticesByAnchor.get(turn.id) ?? [];
    for (const notice of anchoredNotices) {
      entries.push({ kind: 'notice', notice });
    }
    previousTurnRole = anchoredNotices.length > 0 ? null : turn.role;
  }
  for (const notice of orphanNotices) {
    entries.push({ kind: 'notice', notice });
  }
  return entries;
}

export interface TurnStreamProps {
  state: AgentPanelState;
  /**
   * Hooks for chat-history pagination. When ``onLoadOlder`` is set,
   * the stream triggers a fetch when the user nears the top; while
   * that page loads, ``loadingHistory`` shows a small spinner at the
   * top. No static hint is shown when older pages exist. Scroll
   * position is preserved across the prepend so the user stays
   * anchored to the message they were looking at.
   */
  hasMoreHistory?: boolean;
  loadingHistory?: boolean;
  onLoadOlder?: () => void;
  className?: string;
  /**
   * Notified whenever the chat scroll position crosses the
   * "near-bottom" threshold. Lets the parent render scroll
   * affordances (e.g. a "Latest" pill anchored over the compose
   * surface) without owning the scroll DOM itself.
   */
  onAtBottomChange?: (atBottom: boolean) => void;
  /**
   * Click handler for the empty-state starter prompt chips. When
   * provided, the chips become interactive and the callback receives
   * the chosen starter metadata and rich prompt.
   */
  onPickStarter?: (starter: AgentStarterPrompt) => void;
  onRespondToInteraction?: (
    requestId: string,
    decision: 'allow' | 'deny',
    updatedInput?: Record<string, unknown>,
    message?: string
  ) => void;
  canRespondToInteractions?: boolean;
  /**
   * Imperative escape hatch for "scroll to latest" buttons rendered
   * outside the stream. The handle is populated on mount and
   * cleared on unmount; consumers should null-check before calling.
   */
  jumpToBottomRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * Out-parameter for a synchronous "stay at the bottom if already
   * there" pin. Populated on mount with a no-op-otherwise function
   * so neighbors (notably :class:`AgentInput`'s textarea-grow
   * ``useLayoutEffect``) can request a same-frame re-pin when their
   * own layout shrinks the chat viewport. Calling this when the
   * user has scrolled away is a no-op -- intentional scroll
   * position always wins.
   *
   * IMPORTANT: this hook is NOT race-safe against wheel events --
   * it intentionally trusts ``followingTailRef`` and snaps without
   * inspecting ``scrollTop``. The reason is the composer's
   * ``height = 'auto'`` ping-pong corrupts ``scrollTop`` mid-effect
   * (the browser auto-clamps it down by one textarea's worth of
   * pixels), and the race-safe path used by the ResizeObserver
   * would interpret that clamp as a deliberate wheel-up and
   * release follow. Use this only for known synchronous DOM
   * mutations triggered from layout effects.
   */
  keepAtBottomRef?: React.MutableRefObject<(() => void) | null>;
  /** Stop a live background task from an inline task tail. */
  onStopTask?: (taskId: string) => void;
}

/**
 * Stable identifier for "this is the current session/mount that I
 * should initial-pin once for".
 *
 * MUST NOT change when older history is prepended. ``firstTurnId``
 * is the wrong signal here -- it points at ``state.turns[0]``, which
 * gets replaced every time :func:`prependHistory` merges in an older
 * REST page. If we used it as the pin key, every successful "load
 * older messages" would trip the layoutEffect's initial-pin branch
 * and yank the user back to the bottom of the chat -- exactly the
 * regression this helper exists to prevent.
 *
 * ``state.sessionId`` is the right anchor: it's set by
 * ``session_attached`` and stays put for the lifetime of the
 * runtime. The :class:`SessionRuntime` only flips
 * :attr:`sessionReady` (and thus lets ``TurnStream`` mount past the
 * loader) after attach completes, so by the time we render with
 * turns, ``sessionId`` is guaranteed populated.
 *
 * The ``firstTurnId`` fallback covers the pathological edge case
 * where we somehow have turns but no sessionId (attach timeout
 * with cached state, etc.) -- a brand-new session has neither
 * turns nor sessionId, so it stays at ``null`` until the first
 * frame lands.
 */
export function deriveInitialPinKey(
  sessionId: string | null,
  firstTurnId: string | null,
  turnCount: number
): string | null {
  if (sessionId !== null) return sessionId;
  if (turnCount > 0 && firstTurnId !== null) return firstTurnId;
  return null;
}

export function TurnStream({
  state,
  hasMoreHistory = false,
  loadingHistory = false,
  onLoadOlder,
  className,
  onAtBottomChange,
  onPickStarter,
  onRespondToInteraction,
  canRespondToInteractions = false,
  jumpToBottomRef,
  keepAtBottomRef,
  onStopTask,
}: TurnStreamProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const innerRef = React.useRef<HTMLDivElement>(null);
  const topSentinelRef = React.useRef<HTMLDivElement>(null);
  const bottomSentinelRef = React.useRef<HTMLDivElement>(null);

  // ----------------------------- atBottom -----------------------------
  //
  // Shared "Latest chip hidden / auto-follow active" signal.
  // IntersectionObserver keeps it aligned with real geometry after
  // layout shifts; ``handleScroll`` mirrors the same threshold
  // synchronously so a user who decisively scrolls away leaves
  // follow mode before the next resize callback can snap.
  const atBottomRef = React.useRef(true);
  const [atBottom, setAtBottom] = React.useState(true);
  const setAtBottomBoth = React.useCallback((next: boolean) => {
    if (atBottomRef.current === next) return;
    atBottomRef.current = next;
    setAtBottom(next);
  }, []);

  // Coordination flag for the deliberate smooth-pin animation (user
  // send, Latest pill click). While the tween is running we own
  // scrollTop; both observers stay out of the way so the glide
  // isn't replaced with instant snaps on every intermediate frame,
  // and the at-bottom state doesn't flicker mid-glide (so the
  // Latest pill stays visible until the glide lands).
  const pinningRef = React.useRef(false);
  const releasePinningRef = React.useRef<(() => void) | null>(null);
  const releaseInitialPinRef = React.useRef<(() => void) | null>(null);
  const scrollAnchorRef = React.useRef<ScrollAnchor | null>(null);
  const scrollAnchorRafRef = React.useRef(0);

  // Whether the chat should keep snapping to the floor on the next
  // resize / content tick. Released the moment the reader scrolls up
  // off the floor (see ``handleScroll`` + :func:`decideScrollFollow`)
  // and re-engaged only when they come essentially all the way back.
  const followingTailRef = React.useRef(true);

  // ``scrollTop`` at the previous scroll event. ``handleScroll``
  // diffs against it to read scroll *direction*, which is the only
  // signal that separates a deliberate reader wheel-up from
  // streaming growth or a programmatic snap echo.
  const lastScrollTopRef = React.useRef(0);

  // Set immediately before any programmatic ``scrollTop`` write that
  // is NOT a smooth-pin (RO snap, content snap, composer re-pin,
  // initial settle, prepend / remembered-scroll restore). The very
  // next ``handleScroll`` consumes and clears it so our own writes
  // are never misread as a reader wheel-up and never mark the reader
  // as "actively scrolling". Smooth-pin writes are already gated by
  // ``pinningRef``.
  const programmaticScrollRef = React.useRef(false);

  // True between scroll events while the reader is actively dragging.
  // The observers stay out of the way until it clears so they never
  // grab the viewport mid-drag; an idle reconcile re-pins if the
  // reader settled back at the floor.
  const userScrollingRef = React.useRef(false);
  const userScrollIdleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // The most recently submitted user turn carries an entry
  // animation (slide up + fade in) so a sent prompt feels like it
  // was placed by the act of sending rather than instantly
  // appearing. We hold the id only for the animation window so
  // unrelated renders (streaming response, tool rows arriving)
  // don't strip the class mid-flight, and so older user turns
  // never animate when the timeline rebuilds for any reason.
  const [enteringUserTurnId, setEnteringUserTurnId] = React.useState<string | null>(null);

  /**
   * Smooth-scroll the chat to the bottom. Used by the Latest pill
   * click and the layoutEffect's user-just-sent branch -- the two
   * cases where we want a deliberate, visible glide rather than the
   * silent instant pin the ResizeObserver uses for streaming
   * content.
   *
   * After the tween lands we force ``atBottom = true`` ourselves
   * rather than waiting for the IO's async callback -- without that
   * the next ResizeObserver tick (which runs synchronously after
   * layout) can read a stale ``atBottomRef = false`` and bail into
   * the no-op branch, leaving the user momentarily off-tail right
   * after a deliberate jump-to-bottom.
   */
  const updateScrollAnchor = React.useCallback(() => {
    const el = scrollRef.current;
    scrollAnchorRef.current = el ? captureScrollAnchor(el) : null;
  }, []);

  const scheduleScrollAnchorUpdate = React.useCallback(() => {
    if (scrollAnchorRafRef.current !== 0) return;
    scrollAnchorRafRef.current = requestAnimationFrame(() => {
      scrollAnchorRafRef.current = 0;
      updateScrollAnchor();
    });
  }, [updateScrollAnchor]);

  const smoothPinToBottom = React.useCallback(
    (durationMs = SMOOTH_PIN_DURATION_MS) => {
      const el = scrollRef.current;
      if (!el) return;
      releasePinningRef.current?.();
      if (Math.abs(el.scrollTop - bottomScrollTop(el)) < 1) {
        followingTailRef.current = true;
        lastScrollTopRef.current = el.scrollTop;
        updateScrollAnchor();
        setAtBottomBoth(true);
        return;
      }
      pinningRef.current = true;
      let safetyTimer: ReturnType<typeof setTimeout> | undefined;
      let cancelTween: (() => void) | null = null;
      let released = false;
      const cancel = () => {
        if (released) return;
        released = true;
        pinningRef.current = false;
        if (safetyTimer !== undefined) clearTimeout(safetyTimer);
        cancelTween?.();
        if (releasePinningRef.current === cancel) releasePinningRef.current = null;
      };
      const finish = () => {
        if (released) return;
        cancel();
        const landingBottom = bottomScrollTop(el);
        // Mark this as a programmatic write so the scroll event it
        // fires (now that ``pinningRef`` is cleared) isn't misread
        // as a reader wheel-up by ``handleScroll``.
        programmaticScrollRef.current = true;
        el.scrollTop = landingBottom;
        // Re-engage auto-follow now that we landed at the floor.
        followingTailRef.current = true;
        lastScrollTopRef.current = landingBottom;
        updateScrollAnchor();
        setAtBottomBoth(true);
      };
      cancelTween = animateScrollTo(el, () => bottomScrollTop(el), durationMs, finish);
      safetyTimer = setTimeout(finish, durationMs + 100);
      releasePinningRef.current = cancel;
    },
    [setAtBottomBoth, updateScrollAnchor]
  );

  // ----------------- History pagination + initial pin -----------------
  //
  // ``state.turns`` is the canonical transcript. Three orthogonal
  // signals derive from it:
  //   - ``firstTurnId`` change with growing ``turnCount``: older page
  //     was prepended; we capture pre-paint scroll metrics here so the
  //     matching layoutEffect can restore the user's visible offset.
  //   - ``lastTurnId`` change to a user-role turn: the user just sent;
  //     smooth-glide to the bottom regardless of current scroll position.
  //   - ``initialPinKey`` change: session switch / fresh mount; pin to
  //     the bottom with a short settling window for late-loading
  //     content like images. See :func:`deriveInitialPinKey` for why
  //     this MUST be anchored to ``state.sessionId`` rather than
  //     ``firstTurnId`` -- the latter changes on every history prepend
  //     and would yank the user back to the bottom mid-pagination.
  const turnCount = state.turns.length;
  const prevTurnCountRef = React.useRef(0);
  const firstTurnId = state.turns[0]?.id ?? null;
  const prevFirstTurnIdRef = React.useRef<string | null>(null);
  const prependSnapshotRef = React.useRef<{ scrollHeight: number; scrollTop: number } | null>(null);

  const lastTurn = state.turns[state.turns.length - 1] ?? null;
  const lastTurnId = lastTurn?.id ?? null;
  const lastTurnRole = lastTurn?.role ?? null;
  const prevLastTurnIdRef = React.useRef<string | null>(null);

  const initialPinKey = deriveInitialPinKey(state.sessionId, firstTurnId, turnCount);
  const liveContentRevision = state.lastEventAt;
  const initialPinnedKeyRef = React.useRef<string | null>(null);
  const scrollMemoryKey = state.sessionId ?? initialPinKey;
  const rememberedScroll = scrollMemoryKey ? REMEMBERED_SCROLL_POSITIONS.get(scrollMemoryKey) : undefined;
  const shouldRestoreRememberedScroll =
    initialPinKey !== null &&
    initialPinnedKeyRef.current !== initialPinKey &&
    rememberedScroll !== undefined &&
    !rememberedScroll.atBottom &&
    rememberedScroll.revision === state.lastEventAt;
  const shouldInitialPin =
    initialPinKey !== null && initialPinnedKeyRef.current !== initialPinKey && !shouldRestoreRememberedScroll;

  const rememberScrollPosition = React.useCallback(
    (el: HTMLElement) => {
      if (!scrollMemoryKey) return;
      REMEMBERED_SCROLL_POSITIONS.set(scrollMemoryKey, {
        scrollTop: el.scrollTop,
        atBottom: atBottomRef.current,
        revision: state.lastEventAt,
      });
    },
    [scrollMemoryKey, state.lastEventAt]
  );

  // Capture pre-paint scroll metrics whenever turns are about to be
  // prepended. ``useLayoutEffect`` runs after the DOM has new nodes
  // but before the browser paints -- too late to read the *old*
  // height. Instead we read scroll metrics during render (they
  // reflect the previous DOM, since this render hasn't committed
  // yet) and stash them in a ref. The matching ``useLayoutEffect``
  // then reads the post-commit scrollHeight and restores the
  // visible position.
  if (
    !shouldInitialPin &&
    firstTurnId !== prevFirstTurnIdRef.current &&
    turnCount > prevTurnCountRef.current &&
    scrollRef.current &&
    prependSnapshotRef.current === null
  ) {
    prependSnapshotRef.current = {
      scrollHeight: scrollRef.current.scrollHeight,
      scrollTop: scrollRef.current.scrollTop,
    };
  }

  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (shouldRestoreRememberedScroll && rememberedScroll) {
      releaseInitialPinRef.current?.();
      releaseInitialPinRef.current = null;
      programmaticScrollRef.current = true;
      el.scrollTop = Math.min(rememberedScroll.scrollTop, bottomScrollTop(el));
      lastScrollTopRef.current = el.scrollTop;
      // Restored user is somewhere in the middle (we wouldn't have
      // taken this branch otherwise). Pre-seed atBottom = false so
      // the first ResizeObserver tick after restore -- which fires
      // synchronously after layout, before the IO can deliver an
      // updated intersection -- doesn't snap them to the floor from
      // the default atBottom = true. Match for the snap gate too:
      // we're explicitly NOT at the floor, so don't auto-follow.
      followingTailRef.current = false;
      setAtBottomBoth(false);
      updateScrollAnchor();
      initialPinnedKeyRef.current = initialPinKey;
      prevTurnCountRef.current = turnCount;
      prevFirstTurnIdRef.current = firstTurnId;
      prevLastTurnIdRef.current = lastTurnId;
      return;
    }

    if (shouldInitialPin) {
      // Open at the bottom with a settling window for late-loading
      // content (images measuring in, code blocks expanding). The
      // ResizeObserver below also auto-follows once it has
      // ``followingTailRef = true``, but this explicit schedule
      // covers the gap between mount and the first observed
      // resize. Each settling-pin tick marks itself programmatic so
      // the scroll event it fires never reads as a reader wheel-up.
      releaseInitialPinRef.current?.();
      releaseInitialPinRef.current = scheduleSettlingBottomPin(() => {
        const target = bottomScrollTop(el);
        if (Math.abs(el.scrollTop - target) > 0.5) {
          programmaticScrollRef.current = true;
          el.scrollTop = target;
        }
        lastScrollTopRef.current = target;
        updateScrollAnchor();
      });
      followingTailRef.current = true;
      lastScrollTopRef.current = bottomScrollTop(el);
      setAtBottomBoth(true);
      initialPinnedKeyRef.current = initialPinKey;
      prevTurnCountRef.current = turnCount;
      prevFirstTurnIdRef.current = firstTurnId;
      prevLastTurnIdRef.current = lastTurnId;
      return;
    }

    // User prompt just appeared at the tail -- glide to the bottom
    // even if they had been reading history so they can see their
    // sent message and the live reply can stream from there. This
    // wins over a pending prepend snapshot: if the user managed to
    // submit while a history page was loading, the send is the
    // more recent intent and the older content was never the
    // target of the scroll anyway.
    const newUserTurnAppeared =
      lastTurnRole === 'user' && lastTurnId !== null && lastTurnId !== prevLastTurnIdRef.current;
    if (newUserTurnAppeared) {
      // Cancel any in-flight initial-pin settling schedule. On the
      // FIRST message of a fresh chat the ~700ms settle scheduled at
      // session-attach can still be firing; left running it snaps to
      // the floor on every tick while the user message, assistant
      // turn, and WorkingIndicator mount, which reads as the message
      // bouncing up and down before it settles. The send glide is
      // the sole authority for this scroll now.
      releaseInitialPinRef.current?.();
      releaseInitialPinRef.current = null;
      smoothPinToBottom(USER_SUBMIT_PIN_DURATION_MS);
      setEnteringUserTurnId(lastTurnId);
      prependSnapshotRef.current = null;
      prevTurnCountRef.current = turnCount;
      prevFirstTurnIdRef.current = firstTurnId;
      prevLastTurnIdRef.current = lastTurnId;
      return;
    }

    const snapshot = prependSnapshotRef.current;
    if (snapshot) {
      const delta = el.scrollHeight - snapshot.scrollHeight;
      if (delta > 0) {
        programmaticScrollRef.current = true;
        const restoredAnchor = restoreScrollAnchor(el, scrollAnchorRef.current);
        if (!restoredAnchor) {
          el.scrollTop = snapshot.scrollTop + delta;
        }
        // Decide whether to release auto-follow based on where
        // the user WAS before the prepend, not where they are
        // after the shift. Two cases:
        //
        //   * User was reading mid-chat -- snapshot.scrollTop was
        //     well above the old floor. Shift kept them at the
        //     same visible content; they should stay there even
        //     when the agent streams new tokens. Release follow.
        //
        //   * User was inside the Latest-hidden near-tail band and
        //     the older-history IO auto-fired (short / fresh chat).
        //     They never asked to read older content; they just
        //     opened the chat. Keep follow on so the very next
        //     streaming chunk still snaps.
        //
        // Without this branching the auto-pagination loop on a
        // short chat would silently disable auto-follow for the
        // user's entire session: "I'm at the bottom but new
        // messages aren't scrolling in."
        const snapshotMaxScroll = Math.max(0, snapshot.scrollHeight - el.clientHeight);
        const wasNearTail = snapshot.scrollTop >= snapshotMaxScroll - FOLLOW_REENGAGE_BAND_PX;
        if (wasNearTail) {
          // Sync the direction baseline to where we just placed them
          // so the next scroll event isn't read as a wheel-up from a
          // stale ``lastScrollTopRef`` taken before the prepend.
          lastScrollTopRef.current = el.scrollTop;
        } else {
          followingTailRef.current = false;
        }
        updateScrollAnchor();
      }
      prependSnapshotRef.current = null;
    }

    prevTurnCountRef.current = turnCount;
    prevFirstTurnIdRef.current = firstTurnId;
    prevLastTurnIdRef.current = lastTurnId;
  }, [
    turnCount,
    firstTurnId,
    lastTurnId,
    lastTurnRole,
    initialPinKey,
    rememberedScroll,
    shouldRestoreRememberedScroll,
    shouldInitialPin,
    smoothPinToBottom,
    setAtBottomBoth,
    updateScrollAnchor,
  ]);

  // Hold ``enteringUserTurnId`` only for the duration of the entry
  // animation, then clear it so the wrapper class drops off and
  // future timeline rebuilds don't accidentally re-animate the same
  // turn (for example if the reducer replaces the optimistic turn
  // with the server-acknowledged one carrying the same id).
  React.useEffect(() => {
    if (!enteringUserTurnId) return;
    const timer = window.setTimeout(() => setEnteringUserTurnId(null), USER_SUBMIT_ENTER_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [enteringUserTurnId]);

  // -------- IntersectionObserver: geometric at-bottom signal --------
  //
  // The bottom sentinel is a 1px element rendered as the last child
  // of ``innerRef``. With ``rootMargin = '0px 0px ${slop}px 0px'`` the
  // callback fires whenever the reader crosses the show threshold,
  // including via content growth / shrink while they sit still (no
  // scroll event fires for that). It drives the Latest chip ONLY,
  // through the same :func:`decideChipVisible` hysteresis as
  // ``handleScroll`` so the chip can't flicker at the boundary. It
  // never touches ``followingTailRef`` -- follow is owned entirely
  // by ``handleScroll`` reading scroll direction.
  //
  // Gated by ``pinningRef`` so the smooth-pin glide can drive
  // scrollTop without flipping atBottom mid-tween (which would flash
  // the Latest pill).
  React.useEffect(() => {
    const root = scrollRef.current;
    const sentinel = bottomSentinelRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      () => {
        if (pinningRef.current) return;
        const el = scrollRef.current;
        if (!el) return;
        const fromFloor = bottomScrollTop(el) - el.scrollTop;
        setAtBottomBoth(!decideChipVisible(!atBottomRef.current, fromFloor));
      },
      { root, rootMargin: `0px 0px ${AT_BOTTOM_SLOP_PX}px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [setAtBottomBoth]);

  const snapToBottomIfFollowing = React.useCallback(
    (refreshAnchor = false) => {
      if (pinningRef.current) return;
      if (!followingTailRef.current) return;
      // Never grab the viewport while the reader is mid-drag; the
      // idle reconcile re-pins once they settle.
      if (userScrollingRef.current) return;
      const el = scrollRef.current;
      if (!el) return;
      const newBottom = bottomScrollTop(el);
      // Only flag + write when scrollTop actually moves -- writing
      // the same value fires no scroll event, so an unconditional
      // flag would leak and swallow the reader's next wheel-up.
      if (Math.abs(el.scrollTop - newBottom) > 0.5) {
        programmaticScrollRef.current = true;
        el.scrollTop = newBottom;
      }
      lastScrollTopRef.current = newBottom;
      if (refreshAnchor) updateScrollAnchor();
    },
    [updateScrollAnchor]
  );

  /**
   * Variant of :func:`snapToBottomIfFollowing` for callers that
   * just performed a known synchronous DOM mutation that corrupts
   * ``scrollTop`` mid-effect.
   *
   * The canonical caller is the composer's textarea ping-pong
   * (``style.height = 'auto'`` then ``style.height = '${measured}px'``
   * inside one ``useLayoutEffect``). The ``'auto'`` step briefly
   * collapses the textarea to one line, which grows the chat's
   * ``clientHeight``; the browser auto-clamps ``scrollTop`` down to
   * stay inside the new (smaller) scroll range. The second step
   * restores the textarea but the browser does NOT restore
   * ``scrollTop`` -- it stays at the clamped-down value for the
   * rest of the effect. The ping-pong clamp pulls ``scrollTop``
   * DOWN, which a direction-based check would otherwise read as a
   * reader wheel-up and release follow on every Enter press while
   * typing a multi-line message.
   *
   * Trust ``followingTailRef`` instead and mark the re-pin
   * programmatic so the resulting scroll event is ignored. The
   * composer is a known synchronous mutation -- not a wheel event,
   * even though its scrollTop reading looks like one mid-effect.
   */
  const pinToBottomThroughHeightChange = React.useCallback(() => {
    if (pinningRef.current) return;
    if (!followingTailRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const newBottom = bottomScrollTop(el);
    if (Math.abs(el.scrollTop - newBottom) > 0.5) {
      programmaticScrollRef.current = true;
      el.scrollTop = newBottom;
    }
    lastScrollTopRef.current = newBottom;
  }, []);

  // -------- ResizeObserver: snap-or-no-op on any layout change --------
  //
  // Covers every "things resized" scenario in one place:
  //   - assistant tokens stream -> content height grows
  //   - tool blocks auto-expand (Task / Bash active rows)
  //   - thinking blocks reveal text
  //   - images / code blocks measure in late
  //   - composer textarea grows -> chat viewport shrinks
  //   - browser window resizes -> chat container resizes
  //
  // The rule is single-line: following-tail -> snap to the literal
  // floor, scrolled up -> preserve the visible turn anchor.
  // ``followingTailRef`` is updated synchronously in ``handleScroll``
  // from scroll direction, so the resize callback always has a
  // current gate.
  //
  // Gated by ``pinningRef`` (the smooth-pin glide owns scrollTop for
  // its tween) and by ``userScrollingRef`` (a resize mid-drag must
  // not grab the viewport -- the idle reconcile re-pins on settle).
  React.useEffect(() => {
    const root = scrollRef.current;
    const inner = innerRef.current;
    if (!root || !inner) return;
    const observer = new ResizeObserver(() => {
      if (pinningRef.current) return;
      if (userScrollingRef.current) return;
      if (!followingTailRef.current) {
        restoreScrollAnchor(root, scrollAnchorRef.current);
        updateScrollAnchor();
        return;
      }
      snapToBottomIfFollowing(true);
    });
    observer.observe(inner);
    observer.observe(root);
    return () => observer.disconnect();
  }, [snapToBottomIfFollowing, updateScrollAnchor]);

  React.useEffect(() => {
    return () => {
      releasePinningRef.current?.();
      releasePinningRef.current = null;
      releaseInitialPinRef.current?.();
      releaseInitialPinRef.current = null;
      if (userScrollIdleTimerRef.current) {
        clearTimeout(userScrollIdleTimerRef.current);
        userScrollIdleTimerRef.current = null;
      }
      if (scrollAnchorRafRef.current !== 0) {
        cancelAnimationFrame(scrollAnchorRafRef.current);
        scrollAnchorRafRef.current = 0;
      }
      const el = scrollRef.current;
      if (el) rememberScrollPosition(el);
    };
  }, [rememberScrollPosition]);

  // Older-history pagination. An ``IntersectionObserver`` watches
  // a 1px sentinel rendered at the very top of the inner column.
  // The 200px top ``rootMargin`` pre-fetches one screen ahead of
  // the user reaching the actual top edge. The provider's own
  // ``historyLoadingRef`` de-dupes concurrent ``onLoadOlder``
  // calls, and the sentinel is unmounted the moment
  // ``hasMoreHistory`` flips off, so the observer can't run away.
  //
  // No "user must scroll first" gate. Short / fresh chats have
  // their sentinel within the rootMargin band from the very
  // first paint, so the IO auto-loads older pages until the
  // chat is large enough that the sentinel sits outside the
  // band. That closes the catch-22 where a user couldn't scroll
  // up because the chat had no overflow because no older
  // history was loaded because the user couldn't generate a
  // scroll event to arm pagination.
  React.useEffect(() => {
    if (!onLoadOlder || !hasMoreHistory) return;
    const sentinel = topSentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !loadingHistory) {
          scrollAnchorRef.current = captureScrollAnchor(root);
          onLoadOlder();
        }
      },
      { root, rootMargin: '200px 0px 0px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadOlder, hasMoreHistory, loadingHistory]);

  // React commits can make an existing live block taller before the
  // ResizeObserver callback has delivered, most visibly when a
  // thinking preview appears and grows from one row to several. If
  // the reader is following the tail, correct the scrollTop in the
  // same commit so the browser never paints the expanded block
  // detached from the floor. ``snapToBottomIfFollowing`` is a no-op
  // when follow is released or the reader is mid-drag.
  React.useLayoutEffect(() => {
    if (liveContentRevision === 0) return;
    snapToBottomIfFollowing();
  }, [liveContentRevision, snapToBottomIfFollowing]);

  /**
   * Earliest signal we get when the reader's scroll position
   * changes -- via any path (wheel, drag, keyboard, programmatic
   * snap echo).
   *
   * Follow is decided from scroll *direction* via
   * :func:`decideScrollFollow`: a deliberate move up off the floor
   * releases it, settling back at the floor re-engages it, and our
   * own writes (flagged via ``programmaticScrollRef``) are skipped
   * so a snap echo or a content-shrink clamp can never read as a
   * wheel-up. The Latest chip runs through the
   * :func:`decideChipVisible` hysteresis so its boundary can't
   * flicker. The reader is treated as "actively scrolling" until
   * the events go quiet, keeping the observers from grabbing the
   * viewport mid-drag; the idle reconcile re-pins on settle.
   */
  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // The smooth-pin glide owns scrollTop; just keep the direction
    // baseline current so the post-glide reading isn't a false jump.
    if (pinningRef.current) {
      lastScrollTopRef.current = el.scrollTop;
      return;
    }
    const nextTop = el.scrollTop;
    const fromFloor = bottomScrollTop(el) - nextTop;

    // Latest chip (hysteresis) tracks every scroll, ours or not.
    setAtBottomBoth(!decideChipVisible(!atBottomRef.current, fromFloor));

    // Our own programmatic write: sync the baseline and bail before
    // it can be misread as reader intent or mark an active drag.
    if (programmaticScrollRef.current) {
      programmaticScrollRef.current = false;
      lastScrollTopRef.current = nextTop;
      return;
    }

    // Genuine reader scroll.
    const movedUp = lastScrollTopRef.current - nextTop > SCROLL_UP_RELEASE_PX;
    const decision = decideScrollFollow(lastScrollTopRef.current, nextTop, fromFloor, FOLLOW_REENGAGE_BAND_PX);
    if (decision === 'release') followingTailRef.current = false;
    else if (decision === 'reengage') followingTailRef.current = true;
    if (!followingTailRef.current) scheduleScrollAnchorUpdate();
    lastScrollTopRef.current = nextTop;

    // Hold off the observers only while the reader is genuinely
    // moving the viewport, then reconcile on settle. A content-shrink
    // clamp or a steady-stream scroll echo that leaves the reader at
    // the floor must NOT mark an active drag -- doing so would lag
    // the next follow snap by the idle window for no reason.
    if (movedUp || fromFloor > FOLLOW_REENGAGE_BAND_PX) {
      userScrollingRef.current = true;
      if (userScrollIdleTimerRef.current) clearTimeout(userScrollIdleTimerRef.current);
      userScrollIdleTimerRef.current = setTimeout(() => {
        userScrollIdleTimerRef.current = null;
        userScrollingRef.current = false;
        snapToBottomIfFollowing(true);
      }, USER_SCROLL_IDLE_MS);
    }
  }, [scheduleScrollAnchorUpdate, setAtBottomBoth, snapToBottomIfFollowing]);

  React.useEffect(() => {
    onAtBottomChange?.(atBottom);
  }, [atBottom, onAtBottomChange]);

  React.useEffect(() => {
    if (!jumpToBottomRef) return;
    jumpToBottomRef.current = smoothPinToBottom;
    return () => {
      if (jumpToBottomRef.current === smoothPinToBottom) jumpToBottomRef.current = null;
    };
  }, [jumpToBottomRef, smoothPinToBottom]);

  React.useEffect(() => {
    if (!keepAtBottomRef) return;
    keepAtBottomRef.current = pinToBottomThroughHeightChange;
    return () => {
      if (keepAtBottomRef.current === pinToBottomThroughHeightChange) keepAtBottomRef.current = null;
    };
  }, [keepAtBottomRef, pinToBottomThroughHeightChange]);

  // Read the latest reducer state through a ref so the lookup callbacks
  // keep a STABLE identity across SSE deltas. ``state`` is a new object
  // every delta; depending on it (the old ``[state]``) recreated these
  // callbacks every token, which defeats ``React.memo`` on the block
  // views downstream. The underlying selectors return stored slices /
  // primitives, so reading ``stateRef.current`` at call time still yields
  // current data while letting unchanged rows skip re-render.
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const getSubagent = React.useCallback((toolUseId: string) => getSubagentByToolUse(stateRef.current, toolUseId), []);
  const getActiveTask = React.useCallback(
    (toolUseId: string) => getActiveTaskByToolUse(stateRef.current, toolUseId),
    []
  );
  const getToolPending = React.useCallback((toolUseId: string) => isToolUsePending(stateRef.current, toolUseId), []);
  const getToolResult = React.useCallback((toolUseId: string) => stateRef.current.toolResults[toolUseId], []);
  const workingIndicator = getWorkingIndicatorModel(state);
  const compactPending = isCompactPending(state);
  const noResponseHint = shouldShowNoResponseHint(state);
  // Scrollbar visibility note: there used to be an
  // ``agent-scroll-live`` class that hid the scrollbar thumb
  // entirely while the user was at the tail and the agent was
  // streaming, to dampen the thumb flicker
  // ``[scrollbar-gutter:stable]`` couldn't fully suppress. In
  // practice that sent users a much worse signal -- "the
  // scrollbar is gone, so the chat must be frozen" -- and they
  // reported the chat as stuck. The mechanics work fine
  // mid-stream now that the RO is race-safe, so the faded
  // resting scrollbar stays visible at all times.
  const latestAssistantId = React.useMemo(() => latestAssistantTurnId(state.turns), [state.turns]);
  const timeline = React.useMemo(() => buildTimeline(state.turns, state.notices), [state.turns, state.notices]);
  // Whether the tail user turn first appears on THIS render -- i.e. the
  // user just sent. ``prevLastTurnIdRef`` is updated in the layoutEffect
  // (post-commit), so during the mounting render it still holds the
  // previous tail and this reads true exactly on the turn's first paint.
  // The entry-animation class must be present from that first paint;
  // deriving it here (rather than only from ``enteringUserTurnId``, which
  // a layoutEffect sets one commit later) avoids painting the bubble at
  // its final spot for a frame and then snapping it to the slide-in start
  // keyframe -- the visible down-then-up "bounce". Suppressed during the
  // initial-pin / remembered-scroll renders so a transcript that simply
  // ends in a user turn never animates on session open.
  const userTurnJustAppended =
    !shouldInitialPin &&
    !shouldRestoreRememberedScroll &&
    lastTurnRole === 'user' &&
    lastTurnId !== null &&
    lastTurnId !== prevLastTurnIdRef.current;
  // Auto-expand the last few image blocks in the transcript so the
  // chat feels visually live for recent agent screenshots and user
  // paste-ins, without forcing older history pages to download
  // every embedded image. Older image rows still render collapsed
  // and load on click. The set is consumed in ``ImageBlockView``'s
  // initial-state computation only -- the user always wins on
  // expand/collapse decisions after mount.
  const autoExpandImages = React.useMemo(() => collectRecentImageBlocks(state.turns), [state.turns]);
  const showHistoryLoader = loadingHistory && !atBottom;

  return (
    <div className="relative flex-1 overflow-hidden">
      {showHistoryLoader && (
        <div
          className="pointer-events-none absolute top-2 right-0 left-0 z-10 flex justify-center"
          role="status"
          aria-live="polite"
        >
          <div className="rounded-full border border-gray-200/80 bg-white/90 px-2 py-1 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-gray-900/90">
            <Spinner size="size-3.5" className="text-gray-400 dark:text-gray-500" />
            <span className="sr-only">Loading older messages</span>
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={cn(
          // Scroll container fills the overlay width so the gutter sits
          // flush at the right edge. The inner column below caps the
          // content at a comfortable reading width and centers it.
          //
          // ``pb-16`` is the breathing room between the last message
          // and the compose surface when the user is scrolled to the
          // bottom. Living *inside* the scroll container (rather than
          // as ``pt`` on the input wrapper) means it disappears the
          // moment the user scrolls up — older messages then sit
          // flush against the input's small top inset, which is what
          // you want when skimming history.
          'h-full overflow-y-auto overflow-x-hidden px-7 pt-4 pb-16',
          'agent-scroll text-[15px] text-gray-800 dark:text-gray-200',
          '[overflow-anchor:none] [scrollbar-gutter:stable]',
          className
        )}
      >
        <div ref={innerRef} className="mx-auto flex min-h-full w-full max-w-[860px] flex-col gap-2">
          {hasMoreHistory && <div ref={topSentinelRef} aria-hidden className="h-px shrink-0" />}
          {state.turns.length === 0 && state.pendingInteractions.length === 0 && (
            <AgentChatEmptyState onPickStarter={onPickStarter} />
          )}
          {/* Transcript-ordered timeline: turns stay in reducer order,
              with non-init notices inserted after their anchor turn.
              ``InteractionPanels`` and ``WorkingIndicator`` remain at
              the bottom because they reflect live state, not history.
              Wrapped in ``AutoExpandImagesProvider`` so the last few
              image blocks (user-pasted or agent-emitted) auto-expand
              on mount -- older images stay collapsed and load only
              when the reader clicks them. */}
          <AutoExpandImagesProvider value={autoExpandImages}>
            {timeline.map((entry) => {
              if (entry.kind === 'notice') {
                return (
                  <div
                    key={`notice:${entry.notice.id}`}
                    data-turn-stream-anchor={`notice:${entry.notice.id}`}
                    className="[&:empty]:hidden"
                  >
                    <SystemNoticeLine notice={entry.notice} />
                  </div>
                );
              }
              const turn = entry.turn;
              if (turn.role === 'user') {
                // ``animate-in`` plays once on the render where the class
                // first appears -- a sent user prompt slides up by 12px
                // and fades in so the bubble feels placed by the act of
                // sending. Two signals OR'd together: the render-time
                // ``userTurnJustAppended`` puts the class on the very
                // first paint (no bounce), and ``enteringUserTurnId``
                // (set by the layoutEffect, cleared after the hold) keeps
                // it applied for the full animation across the re-renders
                // that streaming triggers. Older user turns (history
                // rebuilds, optimistic→committed id swaps) never animate.
                const entering = turn.id === enteringUserTurnId || (userTurnJustAppended && turn.id === lastTurnId);
                // The first message in a fresh chat has no content above it
                // and no scroll to ride in on, so ``slide-in-from-bottom``
                // reads as a bounce -- the bubble pops in ~12px low and
                // jumps up. Fade it in place instead. Later turns keep the
                // slide: there the rise blends with the scroll that brings
                // the new message into view, so it feels intentional.
                const isFirstTurn = entry.previousTurnRole === null;
                return (
                  <div
                    key={`turn:${turn.id}`}
                    data-turn-stream-anchor={`turn:${turn.id}`}
                    className={cn(
                      '[&:empty]:hidden',
                      entering &&
                        (isFirstTurn
                          ? 'fade-in-0 animate-in duration-300 ease-out'
                          : 'fade-in-0 slide-in-from-bottom-3 animate-in duration-300 ease-out')
                    )}
                  >
                    <UserPrompt turn={turn} compactTop={entry.previousTurnRole === 'user'} />
                  </div>
                );
              }
              return (
                <div
                  key={`turn:${turn.id}`}
                  data-turn-stream-anchor={`turn:${turn.id}`}
                  // ``[&:empty]:hidden`` collapses the wrapper out of
                  // flex layout while ``AssistantStream`` returns
                  // null -- which it does for the brief window
                  // between ``message_start`` (creates an empty
                  // turn) and the first renderable block. Without
                  // this, the parent flex column still applies
                  // ``gap-2`` around the empty wrapper and the
                  // WorkingIndicator below the user message gets
                  // bumped down 8px once the SSE frame lands. Same
                  // collapse logic is on the user/notice wrappers
                  // below so any null-returning child is invisible
                  // to the gap math.
                  className="[&:empty]:hidden"
                >
                  <AssistantStream
                    turn={turn}
                    getToolResult={getToolResult}
                    getSubagent={getSubagent}
                    getActiveTask={getActiveTask}
                    getToolPending={getToolPending}
                    sessionId={state.sessionId}
                    onStopTask={onStopTask}
                    onLayoutShift={snapToBottomIfFollowing}
                    keepThinkingVisible={workingIndicator?.active === true && turn.id === latestAssistantId}
                  />
                </div>
              );
            })}
          </AutoExpandImagesProvider>
          <InteractionPanels
            pending={state.pendingInteractions}
            canRespond={canRespondToInteractions}
            onRespond={onRespondToInteraction ?? NOOP_RESPOND_TO_INTERACTION}
          />
          {noResponseHint && (
            // Inline hint for the "Worked for 0s" / unknown-slash-command
            // case. See :func:`shouldShowNoResponseHint`: only fires when
            // the agent ended idle without producing any output for a
            // user message that begins with ``/``. The hint is small and
            // italic so it reads as a quiet "by the way" rather than a
            // blocking error.
            <div className="rounded-md border border-gray-200/70 bg-gray-50/60 px-3 py-2 text-[12.5px] text-gray-600 italic leading-relaxed dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-300">
              The agent didn't produce a response. If you used a slash command, make sure it's defined under{' '}
              <code className="font-mono text-[12px] not-italic">.claude/commands/&lt;name&gt;.md</code>.
            </div>
          )}
          {compactPending ? (
            <WorkingIndicator active variant="compact" />
          ) : (
            workingIndicator && (
              <WorkingIndicator active={workingIndicator.active} durationMs={workingIndicator.durationMs} />
            )
          )}
          {/* Bottom sentinel for the at-bottom IntersectionObserver.
              MUST be the last child of the inner column so it sits at
              the exact bottom of the scrollable content -- any block
              below it would shift the sentinel out of the viewport
              and break the "at-bottom" detection. ``aria-hidden`` and
              zero height keep it invisible to assistive tech and
              layout-neutral. */}
          <div ref={bottomSentinelRef} aria-hidden className="h-px shrink-0" />
        </div>
      </div>
    </div>
  );
}
